import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { Plus } from 'lucide-react';

/**
 * FAQ — editorial accordion.
 *
 * Single open at a time (first item open by default). Quiet italic Fraunces
 * roman numerals on the left, Fraunces question, thin sage plus-mark that
 * rotates 135° to ×. Hairlines instead of cards.
 *
 * Content (7 Q&A) preserved verbatim from previous version.
 */

// ---------- Content (verbatim) ----------
const FAQS = [
  { q: 'How is Epicurea Nexus different from my CA or accountant?', a: 'Your CA is built for compliance — filing GST, closing books, signing audit reports. We are built for recovery — finding the cash that has been lost to settlement variances, supplier overcharges, and reconciliation gaps, then actively pursuing it back. Different job, different cadence, different tooling. Most of our clients keep their CA and add us as their financial profit-recovery partner.' },
  { q: 'Will my accountant or POS need to change anything?', a: 'No. Your accountant continues their normal Tally workflow. Your POS continues unchanged. We work with whatever data you produce today — we just reconcile it across all three sources in a way no spreadsheet can match.' },
  { q: 'How quickly will I see results?', a: 'A Diagnostic engagement produces a first findings memo within 14 days of kickoff. A Recovery Sprint typically returns the first dispute-settled cash within 30–60 days. Retained Advisory builds month over month — most clients see meaningful recovery in their first calendar quarter with us.' },
  { q: 'What does this cost?', a: 'Diagnostic is fixed-fee (₹1.5–4 lakh depending on outlet complexity). Recovery Sprint combines a retainer with an outcome share — so we only earn the upside if you actually recover the cash. Retained Advisory and Fractional CFO are monthly retainers anchored to the value delivered, not hours billed. Specific pricing is shared during the discovery call.' },
  { q: 'Is my data safe with you?', a: 'Yes. Every engagement starts with an NDA. Your data is encrypted at rest, isolated from every other client, and never used to train shared models or shown to anyone outside our practice. Our internal platform is single-tenant per client at the data layer, and we operate on the principle that your books are your competitive intelligence — not ours.' },
  { q: 'Do you license your software to other consultants?', a: 'No. The platform we use internally is proprietary and stays in-house. We are a consulting practice, not a software vendor — you engage us for the outcome, not the tool. The exclusivity of our platform is part of what makes our work faster and deeper than any spreadsheet-bound competitor.' },
  { q: 'I run just one outlet — am I too small?', a: 'If your annual revenue is above ₹2 crore, the engagement economics make sense. A typical single-outlet client recovers 25–50× the engagement fee within the first 90 days — so even one outlet pays for the engagement many times over.' },
];

// ---------- Editorial bullet ----------
// A single 6-arm snowflake mark used across every FAQ item — quiet sage
// outline that brightens slightly when the item is open.
const Snowflake = ({ stroke }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke={stroke} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
    {/* Three intersecting arms at 0° / 60° / 120° */}
    <path d="M8 1.5 V14.5" />
    <path d="M2.37 4.75 L13.63 11.25" />
    <path d="M2.37 11.25 L13.63 4.75" />
    {/* Small branch ticks near each tip — gives the snowflake its identity */}
    <path d="M8 3.6 L6.7 2.6 M8 3.6 L9.3 2.6" />
    <path d="M8 12.4 L6.7 13.4 M8 12.4 L9.3 13.4" />
    <path d="M4.2 5.8 L3.55 4.3 M4.2 5.8 L2.6 6.0" />
    <path d="M11.8 10.2 L13.4 10.0 M11.8 10.2 L12.45 11.7" />
    <path d="M4.2 10.2 L2.6 10.0 M4.2 10.2 L3.55 11.7" />
    <path d="M11.8 5.8 L12.45 4.3 M11.8 5.8 L13.4 6.0" />
  </svg>
);

// ---------- Palette ----------
const BG          = '#0F0B0A';
const BONE        = '#F2E8D2';
const ANSWER      = 'rgba(242, 232, 210, 0.70)';
const SAGE        = '#9CC6AE';
const NUMERAL     = 'rgba(156, 198, 174, 0.55)';        // matches EngagementProcess numeral tone
const HAIRLINE    = 'rgba(242, 232, 210, 0.12)';

// Expo-out feel for the accordion height/rotation
const EASE = [0.16, 1, 0.3, 1];

// ============================================================
// AccordionItem
// ============================================================
const AccordionItem = ({ item, index, isOpen, onToggle, reduced, isLast }) => {
  return (
    <div
      className="relative"
      style={{ borderBottom: isLast ? 'none' : `0.5px solid ${HAIRLINE}` }}
      data-testid={`faq-item-${index}`}
    >
      <motion.button
        type="button"
        onClick={onToggle}
        whileHover={reduced ? undefined : { x: 8 }}
        transition={{ type: 'spring', stiffness: 250, damping: 24, mass: 0.5 }}
        className="w-full flex items-start gap-5 md:gap-7 py-6 md:py-7 text-left group focus:outline-none"
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${index}`}
      >
        {/* Editorial dingbat — unique per item */}
        <motion.span
          aria-hidden="true"
          animate={
            reduced
              ? undefined
              : isOpen
                ? { rotate: 90, scale: 1.05, color: SAGE }
                : { rotate: 0,  scale: 1,    color: NUMERAL }
          }
          transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 220, damping: 22, mass: 0.5 }}
          className="flex-shrink-0 flex items-center justify-center pt-1.5 select-none"
          style={{ minWidth: '28px', color: NUMERAL }}
        >
          {(() => <Snowflake stroke="currentColor" />)()}
        </motion.span>

        {/* Question */}
        <span
          className="flex-1 leading-snug tracking-tight"
          style={{
            fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
            fontWeight: 500,
            fontSize: '19px',
            color: BONE,
          }}
        >
          {item.q}
        </span>

        {/* Plus → × marker */}
        <motion.span
          aria-hidden="true"
          animate={{ rotate: isOpen ? 135 : 0 }}
          transition={reduced ? { duration: 0 } : { duration: 0.45, ease: EASE }}
          className="flex-shrink-0 flex items-center justify-center pt-1"
          style={{ color: SAGE }}
        >
          <Plus className="w-[18px] h-[18px]" strokeWidth={1.5} />
        </motion.span>
      </motion.button>

      {/* Answer — expands with eased height animation */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={reduced ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduced ? { height: 0, opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduced ? { duration: 0 } : {
              height:  { duration: 0.55, ease: EASE },
              opacity: { duration: 0.4,  ease: EASE, delay: 0.05 },
            }}
            className="overflow-hidden"
          >
            <p
              className="pl-[48px] md:pl-[60px] pr-4 pb-7 md:pb-8 max-w-3xl"
              style={{
                color: ANSWER,
                fontFamily: "'Manrope', system-ui, sans-serif",
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: 1.7,
              }}
            >
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// Section
// ============================================================
export const FAQ = () => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  // First item open by default; clicking opens that one and closes any other.
  // Clicking an already-open item collapses it (so it's possible to have all closed).
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id="faq"
      data-testid="landing-faq"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{ backgroundColor: BG }}
    >
      {/* Subtle film grain */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='2' seed='13' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Asymmetric warm wash, upper-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 82% 20%, rgba(156, 198, 174, 0.05) 0%, rgba(156, 198, 174, 0.02) 35%, rgba(15, 11, 10, 0) 70%)',
        }}
      />

      <div ref={ref} className="relative mx-auto max-w-4xl px-6 md:px-10">
        {/* Editorial header */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
          transition={
            reduced
              ? { duration: 0.5 }
              : { type: 'spring', stiffness: 80, damping: 19, mass: 0.9 }
          }
          className="max-w-3xl mb-16 md:mb-20"
        >
          <div
            className="text-[10px] md:text-xs uppercase tracking-[0.34em] font-medium mb-6 flex items-center gap-3"
            style={{ color: 'rgba(156, 198, 174, 0.72)' }}
          >
            <span className="block w-10 h-px" style={{ background: SAGE, opacity: 0.5 }} />
            Frequently Asked
          </div>
          <h2
            className="tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-[60px]"
            style={{ color: BONE, fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif", fontWeight: 500 }}
          >
            Questions owners ask us
            <span className="block italic" style={{ color: SAGE }}>
              before the first call.
            </span>
          </h2>
        </motion.div>

        {/* Accordion list */}
        <motion.div
          initial={reduced ? false : { opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={reduced ? { duration: 0.3 } : { duration: 0.6, delay: 0.18, ease: EASE }}
          className="relative"
          style={{ borderTop: `0.5px solid ${HAIRLINE}` }}
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              reduced={!!reduced}
              isLast={i === FAQS.length - 1}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
